import type { Position } from './position-storage';

export interface Viewport {
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface InitialPositionSpec {
  edge: 'left' | 'right';
  /** "50%", "25%", "100px" etc. */
  offset: string;
  /** Which end of the vertical axis `offset` is measured from. Default: 'top'. */
  anchorY?: 'top' | 'bottom';
}

/**
 * Compute the initial top-left {x, y} for the button based on a pinned edge
 * and an offset along that edge.
 */
export function computeInitialPosition(
  spec: InitialPositionSpec,
  viewport: Viewport,
  size: Size,
): Position {
  const x = spec.edge === 'right' ? viewport.width - size.width : 0;
  const fromTop = resolveOffset(spec.offset, viewport.height, size.height);
  // 'bottom': mirror the top-relative offset across the axis so "24px" means
  // "24px above the bottom edge" instead of "24px below the top edge".
  const y = (spec.anchorY ?? 'top') === 'bottom' ? viewport.height - size.height - fromTop : fromTop;
  return { x, y };
}

function resolveOffset(offset: string, axisLength: number, sizeAlongAxis: number): number {
  if (offset.endsWith('%')) {
    const pct = parseFloat(offset.slice(0, -1));
    return axisLength * (pct / 100) - sizeAlongAxis / 2;
  }
  if (offset.endsWith('px')) {
    return parseFloat(offset.slice(0, -2));
  }
  // Fallback: treat as raw number string
  const n = parseFloat(offset);
  return isNaN(n) ? 0 : n;
}

/**
 * Clamp a position so the button stays fully inside the viewport.
 */
export function clampToViewport(pos: Position, viewport: Viewport, size: Size): Position {
  const maxX = Math.max(0, viewport.width - size.width);
  const maxY = Math.max(0, viewport.height - size.height);
  return {
    x: Math.min(Math.max(0, pos.x), maxX),
    y: Math.min(Math.max(0, pos.y), maxY),
  };
}

/**
 * Re-anchor a position after the viewport or the element itself changed size.
 *
 * A stored {x, y} means "anchored to an edge", not "at this pixel". Clamping
 * alone is one-directional: it rescues a button that fell outside a shrinking
 * viewport, but leaves it stranded mid-screen when the viewport grows back.
 * So we measure the gap to the *nearer* edge on each axis and preserve that
 * gap, which keeps an edge-snapped button snapped and keeps a button parked
 * 24px above the bottom exactly 24px above the bottom.
 */
export function reanchorToNearestEdges(
  pos: Position,
  from: { viewport: Viewport; size: Size },
  to: { viewport: Viewport; size: Size },
): Position {
  return {
    x: reanchorAxis(
      pos.x,
      from.viewport.width,
      from.size.width,
      to.viewport.width,
      to.size.width,
    ),
    y: reanchorAxis(
      pos.y,
      from.viewport.height,
      from.size.height,
      to.viewport.height,
      to.size.height,
    ),
  };
}

/**
 * Single-axis re-anchor. `fromLen`/`toLen` are the viewport extent along the
 * axis, `fromSize`/`toSize` are the element's extent along the axis.
 */
function reanchorAxis(
  pos: number,
  fromLen: number,
  fromSize: number,
  toLen: number,
  toSize: number,
): number {
  const fromAvailable = fromLen - fromSize;
  const toAvailable = toLen - toSize;

  // Guard: if either geometry has no room to spare (element as big as or
  // bigger than the viewport), a "gap to the nearest edge" isn't a
  // meaningful concept — fall back to a plain clamp so we never hand back a
  // negative coordinate.
  if (fromAvailable <= 0 || toAvailable <= 0) {
    return Math.min(Math.max(0, pos), Math.max(0, toAvailable));
  }

  const gapToStart = pos;
  const gapToEnd = fromAvailable - pos;
  // Ties (exactly centered) keep the start edge as the anchor.
  const anchoredAtStart = gapToStart <= gapToEnd;
  const gap = anchoredAtStart ? gapToStart : gapToEnd;
  const next = anchoredAtStart ? gap : toAvailable - gap;

  return Math.min(Math.max(0, next), toAvailable);
}

/**
 * Whether the cursor has moved far enough from its starting point to count
 * as a drag (versus a click). Uses Euclidean distance for natural feel.
 */
export function exceedsDragThreshold(
  start: Position,
  current: Position,
  thresholdPx: number,
): boolean {
  const dx = current.x - start.x;
  const dy = current.y - start.y;
  return Math.sqrt(dx * dx + dy * dy) > thresholdPx;
}
